<template>
  <header class="support-ticket-modal-header">
    <div class="support-ticket-modal-header__identity">
      <div class="support-ticket-modal-header__copy">
        <span class="support-ticket-modal-header__number">{{ ticket ? ticket.public_number : 'Support ticket' }}</span>
        <div class="support-ticket-modal-header__subject-row">
          <h2 id="ticket-workspace-title" :title="ticket ? ticket.subject : ''">{{ ticket ? ticket.subject : 'Loading ticket…' }}</h2>
          <button
            v-if="ticket && canEdit"
            type="button"
            class="support-ticket-modal-header__edit"
            aria-label="Edit ticket title"
            title="Edit ticket title"
            :disabled="working"
            @click="$emit('edit-title')"
          ><img src="@/assets/img/edit-table-icon.svg" alt=""></button>
          <app-status-badge
            v-if="ticket"
            design="new"
            class="support-ticket-modal-header__status"
            :label="statusLabel(ticket.status)"
            :tone="statusTone(ticket.status)"
          />
          <ticket-priority-indicator v-if="ticket" class="support-ticket-modal-header__priority" :priority="ticket.priority" hide-medium />
          <button
            v-if="ticket && canToggleImportant"
            type="button"
            class="support-ticket-modal-header__important"
            :class="{ 'support-ticket-modal-header__important--active': ticket.is_important }"
            :aria-label="ticket.is_important ? 'Remove important mark' : 'Mark as important'"
            :aria-pressed="String(ticket.is_important)"
            :title="ticket.is_important ? 'Remove important mark' : 'Mark as important'"
            :disabled="working"
            @click="$emit('toggle-important')"
          ><img :src="ticket.is_important ? require('@/assets/img/star-full.svg') : require('@/assets/img/star-empty.svg')" alt=""></button>
        </div>
      </div>
    </div>

    <div class="support-ticket-modal-header__actions">
      <div v-if="ticket" class="support-ticket-modal-header__requester-wrap">
        <div class="support-ticket-modal-header__requester" :title="`Requested by ${requesterName}`">
          <img v-if="requesterAvatar" :src="requesterAvatar" alt="">
          <span v-else aria-hidden="true">{{ requesterInitials }}</span>
          <strong>{{ requesterName }}</strong>
        </div>
        <button
          v-if="canEdit"
          type="button"
          class="support-ticket-modal-header__edit"
          aria-label="Edit requester"
          title="Edit requester"
          :disabled="working"
          @click="$emit('edit-requester')"
        ><img src="@/assets/img/edit-table-icon.svg" alt=""></button>
      </div>
      <app-modal-close design="new" :label="`Close ${ticket ? ticket.subject : 'ticket'}`" @click="$emit('close')" />
    </div>
  </header>
</template>

<script>
import AppModalClose from '@/components/AppModalClose'
import { statusLabel, statusTone } from '../ticketState'
import TicketPriorityIndicator from './TicketPriorityIndicator'

export default {
  name: 'SupportTicketModalHeader',
  components: { AppModalClose, TicketPriorityIndicator },
  props: {
    ticket: { type: Object, default: null },
    canEdit: Boolean,
    canToggleImportant: Boolean,
    working: Boolean
  },
  computed: {
    requester() { return this.ticket?.requester || null },
    requesterName() { return this.requester?.full_name || 'Unknown requester' },
    requesterAvatar() { return this.requester?.crop?.crop_url || '' },
    requesterInitials() {
      return this.requesterName.split(' ').filter(Boolean).slice(0, 2).map(part => part.charAt(0).toUpperCase()).join('') || '—'
    }
  },
  methods: { statusLabel, statusTone }
}
</script>

<style lang="scss" scoped>
.support-ticket-modal-header {
  position: sticky;
  z-index: 4;
  top: 0;
  display: flex;
  min-height: 84px;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 20px 24px;
  border-bottom: 1px solid $ui-line;
  background: $ui-surface;
  box-shadow: 0 3px 14px rgba(28, 45, 74, .06);
}

.support-ticket-modal-header__identity,
.support-ticket-modal-header__actions,
.support-ticket-modal-header__requester-wrap,
.support-ticket-modal-header__requester { display: flex; min-width: 0; align-items: center; }
.support-ticket-modal-header__identity { flex: 1 1 auto; }
.support-ticket-modal-header__copy { display: grid; min-width: 0; gap: 3px; }
.support-ticket-modal-header__number { color: $ui-blue; font-family: $ui-font-heading; font-size: 12px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; }
.support-ticket-modal-header__subject-row { display: flex; min-width: 0; align-items: center; gap: 11px; }
.support-ticket-modal-header__subject-row h2 { min-width: 0; overflow: hidden; margin: 0; color: $ui-ink; font-family: $ui-font-heading; font-size: 24px; font-weight: 600; line-height: 30px; text-overflow: ellipsis; white-space: nowrap; }
.support-ticket-modal-header__status { display: inline-flex; max-width: 150px; min-height: 25px; flex: 0 0 auto; align-items: center; overflow: hidden; padding: 4px 10px; border-radius: 13px; color: #fff; font-size: 11px; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
.support-ticket-modal-header__priority { flex: 0 0 auto; }
.support-ticket-modal-header__important { display: inline-flex; width: 30px; height: 30px; flex: 0 0 30px; align-items: center; justify-content: center; padding: 0; border: 1px solid transparent; border-radius: 50%; background: transparent; cursor: pointer; opacity: .42; transition: background-color .16s ease, opacity .16s ease, transform .16s ease; }
.support-ticket-modal-header__important:hover { background: rgba($ui-amber, .1); opacity: .82; transform: translateY(-1px) scale(1.07); }
.support-ticket-modal-header__important:focus-visible { outline: 2px solid $ui-amber; outline-offset: 2px; opacity: 1; }
.support-ticket-modal-header__important--active { background: rgba($ui-amber, .14); opacity: 1; }
.support-ticket-modal-header__important--active:hover { background: rgba($ui-amber, .2); opacity: 1; }
.support-ticket-modal-header__important:disabled { cursor: wait; opacity: .35; transform: none; }
.support-ticket-modal-header__important img { width: 18px; height: 18px; }
.support-ticket-modal-header__edit { display: inline-flex; width: 28px; height: 28px; flex: 0 0 28px; align-items: center; justify-content: center; padding: 0; border: 0; border-radius: 50%; background: transparent; cursor: pointer; opacity: .65; }
.support-ticket-modal-header__edit:hover { background: $ui-blue-soft; opacity: 1; }
.support-ticket-modal-header__edit:focus-visible { outline: 2px solid $ui-blue; outline-offset: 2px; opacity: 1; }
.support-ticket-modal-header__edit:disabled { cursor: wait; opacity: .3; }
.support-ticket-modal-header__edit img { width: 15px; height: 15px; }
.support-ticket-modal-header__actions { flex: 0 0 auto; gap: 10px; }
.support-ticket-modal-header__requester-wrap { gap: 3px; }
.support-ticket-modal-header__requester { max-width: 240px; gap: 8px; color: $ui-muted; font-size: 12px; }
.support-ticket-modal-header__requester img,
.support-ticket-modal-header__requester > span { display: inline-flex; width: 30px; height: 30px; flex: 0 0 30px; align-items: center; justify-content: center; border-radius: 50%; }
.support-ticket-modal-header__requester img { object-fit: cover; }
.support-ticket-modal-header__requester > span { border-radius: 8px; background: $ui-blue-soft; color: $ui-blue; font-size: 10px; font-weight: 700; }
.support-ticket-modal-header__requester strong { overflow: hidden; font-family: $ui-font-heading; font-size: 14px; font-weight: 600; line-height: 20px; text-overflow: ellipsis; white-space: nowrap; }

@media (max-width: 760px) {
  .support-ticket-modal-header { align-items: flex-start; flex-wrap: wrap; padding-right: 16px; padding-left: 16px; }
  .support-ticket-modal-header__identity { width: 100%; }
  .support-ticket-modal-header__actions { width: 100%; justify-content: flex-end; }
}
</style>
